import { addDays, addMonths, format, isWeekend, startOfMonth } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
const dateTimeFormatter = Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export const formatDateTime = (datetime: number | Date | string): string =>
  dateTimeFormatter.format(new Date(datetime));

export const formatDateOnlyUtc = (datetime: number | Date | string): string =>
  formatInTimeZone(new Date(datetime), 'UTC', 'MMM d, yyyy');

/** Formats a YYYY-MM-DD date string as long date in UTC (e.g. "Friday, March 13, 2026"). */
export const formatDateOnlyLongUtc = (dateString: string): string =>
  formatInTimeZone(
    new Date(`${dateString}T12:00:00Z`),
    'UTC',
    'EEEE, MMMM d, yyyy'
  );

export const formatDate = (
  date: string,
  outputFormat = 'PP',
  localize = false
): string => {
  if (localize) return format(getLocalizedDate(date), outputFormat);
  return format(new Date(date), outputFormat);
};

export const getLocalizedDate = (dateString: string): Date => {
  const date = new Date(dateString + 'T00:00:00Z');
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() + timezoneOffset);
  return localDate;
};

export function toStartOfDayUTC(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

export function toEndOfDayUTC(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T23:59:59.999Z`;
}

export function utcDateStringToLocalDate(isoString: string): Date {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return new Date();
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function filtersToDateRange(start: string[], end: string[]) {
  const ti = start.length
    ? new Date(decodeURIComponent(start[0])).toISOString()
    : undefined;
  const tf = end.length
    ? new Date(decodeURIComponent(end[0])).toISOString()
    : undefined;
  return { start: ti, end: tf };
}

export const toDateFilter = (d: string[]) => {
  if (!d || !d.length) {
    return undefined;
  }
  return decodeURIComponent(d[0]);
};

export type DateTimeFormatType = 'VOOMA_UI';

export const formatDateTimeByType = (
  standardDateTime: {
    dateTime: string;
    timezone?: string;
    timeZone?: string;
    interpretationPolicy: 'DATE' | 'DATE_AND_TIME';
  },
  formatType: DateTimeFormatType
): string | undefined => {
  const tz = standardDateTime.timezone || standardDateTime.timeZone;
  const date = new Date(standardDateTime.dateTime);

  switch (formatType) {
    case 'VOOMA_UI':
      if (standardDateTime.interpretationPolicy === 'DATE_AND_TIME') {
        if (tz) {
          return formatInTimeZone(date, tz, 'MMM d, yyyy h:mm a');
        } else {
          return formatInTimeZone(date, 'UTC', 'MMM d, yyyy h:mm a') + ' (UTC)';
        }
      } else {
        if (tz) {
          return formatInTimeZone(date, tz, 'MMM d, yyyy');
        } else {
          return formatInTimeZone(date, 'UTC', 'MMM d, yyyy') + ' (UTC)';
        }
      }

    default:
      throw new Error(`Unsupported format type: ${formatType}`);
  }
};

export const formatDateTimeForDisplay = (
  standardDateTime?: {
    dateTime: string;
    timeZone?: string;
    interpretationPolicy: 'DATE' | 'DATE_AND_TIME';
  },
  formatType: DateTimeFormatType = 'VOOMA_UI'
): string | undefined => {
  if (!standardDateTime) {
    return undefined;
  }
  return formatDateTimeByType(standardDateTime, formatType);
};

export function firstWeekdayOfNextMonth(now: Date, timezone: string): string {
  let date = startOfMonth(addMonths(toZonedTime(now, timezone), 1));
  while (isWeekend(date)) date = addDays(date, 1);
  return format(date, 'yyyy-MM-dd');
}
